import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import { MediaResponse, Person } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { downloadLink } from 'utils/util/util';
import { AdvancedCurrentPeopleFilterRoleUserApiComponent } from 'views/Widgets/Filters/AdvancedSpecializedFilter/highOrder';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  documentId: number;
  onSuccess: (document: Document) => void;
}

interface Document {
  id: number;
  name: string;
  description: string;
  visibility: 'I' | 'P';
  media: {
    id: number;
    file: string;
    filename: string;
  };
  owners: Person[];
  viewers?: unknown;
}

interface FormObject {
  id: number;
  name: string;
  description: string;
  visibility: 'I' | 'P';
  media: Array<{
    id: number;
    file: string;
    filename: string;
  }>;
  owners: Person[];
  viewers?: unknown;
}

const EditDocumentModal: FC<Props> = ({
  isOpen,
  toggle,
  documentId,
  onSuccess,
}) => {
  const { formatMessage } = useIntl();
  const [document, setDocument] = useState<FormObject | null>(null);

  const { data, status } = useConfirmApi<Document>({
    url: `documents/${documentId}`,
    method: 'GET',
    disabled: !isOpen,
  });

  useEffect(() => {
    if (data) {
      setDocument({ ...data, media: [data.media] });
    }
  }, [data]);

  const handleCallback = useCallback(
    (responseData: Document, error: unknown) => {
      console.log('responseData', responseData, 'error', error);
      if (responseData) {
        toast.success(
          formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.edit_modal.success',
            defaultMessage: 'Document saved successfully',
          })
        );
        onSuccess(responseData);
        // toggle();
      } else if (error) {
        toast.error(
          formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.edit_modal.error',
            defaultMessage: 'Failed to save document',
          })
        );
      }
    },
    [formatMessage, onSuccess]
  );

  const transformObjectBeforeSubmit = useCallback(
    (obj: Record<string, unknown>) => {
      return {
        ...obj,
        media: obj.media
          ? (obj.media as Array<{ id: string }>).map((m) => m.id)[0]
          : undefined,
        owners: ((obj.owners ?? []) as Person[]).map(
          (owner: Person) => owner.id
        ),
        viewers: obj.viewers || null,
      };
    },
    []
  );

  const renderInputs = useCallback(
    ([name, description, owners, visibility, viewers, media]) => {
      const visibilityValue =
        visibility.props.value ?? visibility.props.defaultValue;
      return (
        <>
          {name}
          {description}
          {owners}
          {visibility}
          {visibilityValue === 'C' && viewers}
          {media}
        </>
      );
    },
    []
  );

  const handleDownload = useCallback((file: string) => {
    downloadLink({
      filename: file.split('/').pop() || 'document',
      url: file,
    });
  }, []);

  const inputs = useMemo(
    () => [
      {
        required: true,
        name: 'name',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.name.label',
          defaultMessage: 'Document Name',
        }),
        placeholder: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.name.placeholder',
          defaultMessage: 'Enter document name',
        }),
        type: INPUT_TYPES.TEXT,
        autoFocus: true,
        ignorePasswordManagers: true,
      },
      {
        required: false,
        name: 'description',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.description.label',
          defaultMessage: 'Description',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.description.help_text',
          defaultMessage: 'Provide a brief description of the document.',
        }),
        placeholder: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.description.placeholder',
          defaultMessage: 'Enter a description (optional)',
        }),
        type: INPUT_TYPES.TEXTAREA,
      },
      {
        required: true,
        allowSelf: true,
        name: 'owners',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.owners.label',
          defaultMessage: 'Owners',
        }),
        type: INPUT_TYPES.PEOPLE_EDITOR,
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.owners.help_text',
          defaultMessage:
            'Select people who can manage this document. If left empty, you will be set as the owner.',
        }),
      },
      {
        required: true,
        name: 'visibility',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.visibility.label',
          defaultMessage: 'Visibility',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.visibility.help_text',
          defaultMessage:
            'Set the visibility that will determine who can access the document. The people with this access will be able to view it but not edit it. If set to custom, you must define a viewer filter.',
        }),
        type: INPUT_TYPES.DROPDOWN,
        objects: [
          {
            id: 'I',
            name: formatMessage({
              id: 'app.views.administration.content_administration.documents_admin.visibility.private',
              defaultMessage: 'Private',
            }),
          },
          {
            id: 'P',
            name: formatMessage({
              id: 'app.views.administration.content_administration.documents_admin.visibility.public',
              defaultMessage: 'Public',
            }),
          },
          {
            id: 'C',
            name: formatMessage({
              id: 'app.views.administration.content_administration.documents_admin.visibility.custom',
              defaultMessage: 'Custom',
            }),
          },
        ],
      },
      {
        required: false,
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: AdvancedCurrentPeopleFilterRoleUserApiComponent,
        name: 'viewers',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.viewers.label',
          defaultMessage: 'Viewers',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.viewers.help_text',
          defaultMessage:
            'Use advanced filters to specify which people can access this document.',
        }),
      },
      {
        required: true,
        name: 'media',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.media.label',
          defaultMessage: 'File',
        }),
        type: INPUT_TYPES.MEDIA_UPLOADER,
        uploadUrl: process.env.REACT_APP_CONFIRM_API_URL + '/files/document',
        callToAction: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.media.call_to_action',
          defaultMessage: 'Upload new file to replace current',
        }),
        multiple: true,
        maxFiles: 1,
        mediaResponseMapper: (mediaResponse: MediaResponse) => ({
          id: mediaResponse.media.id,
          description: mediaResponse?.description,
        }),
        helperText: formatMessage(
          {
            id: 'app.views.administration.content_administration.documents_admin.edit_modal.fields.media.helper_text',
            defaultMessage: 'Current file: {filename} <link>Download</link>',
          },
          {
            filename: document?.media[0]?.file.split('/').pop(),
            link: (chunks: React.ReactNode) => (
              <Button
                color="link"
                size="sm"
                onClick={() =>
                  document?.media[0]?.file &&
                  handleDownload(document.media[0].file)
                }
                className="p-0"
              >
                <i className="fe fe-download me-1" />
                {chunks}
              </Button>
            ),
          }
        ),
      },
    ],
    [formatMessage, document?.media, handleDownload]
  );

  const handleOnValidate = useCallback(
    (obj) => {
      if (obj.visibility === 'C' && !obj.viewers) {
        return {
          viewers: formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.edit_modal.validation.viewers_required',
            defaultMessage:
              'Please specify a filter when visibility is set to custom.',
          }),
        };
      }
      return {};
    },
    [formatMessage]
  );

  if (status === 'LOADING' || !document) {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.edit_modal.loading',
          defaultMessage: 'Loading document...',
        })}
      />
    );
  }

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      object={document}
      inputs={inputs}
      method="PATCH"
      url={`documents`}
      submitText={formatMessage({
        id: 'app.views.administration.content_administration.documents_admin.edit_modal.submit_text',
        defaultMessage: 'Save Changes',
      })}
      title={formatMessage({
        id: 'app.views.administration.content_administration.documents_admin.edit_modal.title',
        defaultMessage: 'Edit Document',
      })}
      callback={handleCallback}
      onValidate={handleOnValidate}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      renderInputs={renderInputs}
      keepOpenOnSuccess={true}
    />
  );
};

export default EditDocumentModal;
